/**
 * Helpers compartidos por las vistas de relatorio.
 */

import i18n, { t } from '../languages.js';

// Rango por defecto: últimos 30 días (lo más útil para un promotor que revisa
// su captación reciente). Devuelve YYYY-MM-DD.
export function rangoPorDefecto() {
    const hoy = new Date();
    const hace30 = new Date(hoy.getTime() - 30 * 86400000);
    return { desde: iso(hace30), hasta: iso(hoy) };
}
function iso(d) {
    return d.toISOString().split('T')[0];
}

// SSOT del locale por idioma (para separadores de miles/decimales).
function localeDelIdioma() {
    const lang = (i18n && i18n.currentLanguage) || 'es';
    return { es: 'es-PE', pt: 'pt-BR', en: 'en-US', ru: 'ru-RU' }[lang] || 'es-PE';
}

// Monto en céntimos → display con separador de miles, sin símbolo de moneda
// (la moneda se infiere del contexto; el promotor opera en la suya). Usa el
// locale del idioma activo para el separador.
export function montoDisplay(centimos) {
    const valor = (Number(centimos) || 0) / 100;
    try {
        return valor.toLocaleString(localeDelIdioma(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (_) {
        return valor.toFixed(2);
    }
}

// Monto HÉROE en céntimos → HTML con el entero GRANDE y los centavos reducidos
// (patrón fintech: el entero es el protagonista, ',00' lo acompaña al 45%). El
// contenedor debe tener clase .hero-monto; fitty lo escala para llenar el ancho.
// Reusa el locale de montoDisplay (no duplica).
export function montoHeroHtml(centimos) {
    const display = montoDisplay(centimos); // ej. "1.250,00" o "1,250.00"
    // Separar parte entera de la decimal (los 2 últimos dígitos tras el separador).
    const m = display.match(/^(.*)([.,]\d{2})$/);
    if (!m) return display;
    const entero = m[1];
    const centavos = m[2];
    return `${entero}<span class="cents">${centavos}</span>`;
}

// Teléfono legible para contactar: agrupa en bloques sin alterar los dígitos.
// El promotor necesita reconocer a su jugador de un vistazo antes de escribirle.
export function telefonoLegible(telefono) {
    if (!telefono) return '—';
    const digitos = String(telefono).replace(/\D/g, '');
    // Brasil: 55 + DDD(2) + número(8-9) → +55 (11) 98765-4321
    if (digitos.startsWith('55') && digitos.length >= 12) {
        const ddd = digitos.slice(2, 4);
        const resto = digitos.slice(4);
        const corte = resto.length > 8 ? 5 : 4;
        return `+55 (${ddd}) ${resto.slice(0, corte)}-${resto.slice(corte)}`;
    }
    return String(telefono);
}

// Estado de repaso del afiliado — clasifica por lo que HIZO con su dinero, no por
// actividad. Es el criterio con el que el promotor decide a quién escribir:
//   fiel:      recargó y ya cobró → comprobó que la casa paga, es su mejor cliente
//   invirtio:  recargó y no cobró → sostiene la operación
//   porCobrar: nunca recargó pero tiene premio ganado → la lista más fácil de todas
//   soloBono:  gastó el bono y no volvió
export const GRUPO_REPASO = {
    TODOS: 'todos',
    FIEL: 'fiel',
    INVIRTIO: 'invirtio',
    POR_COBRAR: 'porCobrar',
    SOLO_BONO: 'soloBono',
};

export function grupoRepasoDelAfiliado(afiliado) {
    const recargo = (afiliado.total_recargado || 0) > 0;
    const cobro = (afiliado.total_cobrado || 0) > 0;
    if (recargo && cobro) return GRUPO_REPASO.FIEL;
    if (recargo) return GRUPO_REPASO.INVIRTIO;
    if ((afiliado.premios_pagados || 0) > 0) return GRUPO_REPASO.POR_COBRAR;
    return GRUPO_REPASO.SOLO_BONO;
}

// Días desde el último repaso; null si nunca fue repasado.
export function diasDesdeUltimoRepaso(afiliado) {
    if (!afiliado.ultimo_repaso) return null;
    const ms = Date.now() - Date.parse(afiliado.ultimo_repaso + 'Z');
    if (!Number.isFinite(ms) || ms < 0) return null;
    return Math.floor(ms / 86400000);
}

// Descanso entre repasos al mismo jugador: escribirle todos los días lo quema.
// Si fue repasado hace menos de esto y NO volvió, la prioridad lo saltea y el
// botón se atenúa (no se bloquea — el promotor conoce a su gente).
export const DIAS_DESCANSO_ENTRE_REPASOS = 5;

// Prioridad de contacto = valor del jugador × urgencia de la ventana.
//
// La urgencia manda: la probabilidad de recuperar a un jugador cae de 27% el
// día 1 a 2% a los 90 días (benchmark de industria sobre 5,3M de jugadores).
// Por eso un fiel que lleva 2 días parado vale más que cualquier otro contacto
// del día, aunque otro haya jugado más volumen.
//
// Ventana por días de inactividad: 1-3 días ×3 (caliente) · 4-7 ×2 · 8-30 ×1 ·
// resto 0. Excepción: "por cobrar" nunca baja de ×1 — un premio sin retirar no
// caduca como oportunidad de contacto.
const PESO_VALOR_GRUPO = {
    [GRUPO_REPASO.FIEL]: 4,
    [GRUPO_REPASO.INVIRTIO]: 3,
    [GRUPO_REPASO.POR_COBRAR]: 2,
    [GRUPO_REPASO.SOLO_BONO]: 1,
};

export function prioridadDeContacto(afiliado) {
    const diasRepaso = diasDesdeUltimoRepaso(afiliado);
    if (diasRepaso !== null && diasRepaso < DIAS_DESCANSO_ENTRE_REPASOS && !afiliado.repaso_funciono) {
        return 0;
    }
    const grupo = grupoRepasoDelAfiliado(afiliado);
    const dias = afiliado.dias_inactivo;
    let ventana = 0;
    if (typeof dias === 'number') {
        if (dias >= 1 && dias <= 3) ventana = 3;
        else if (dias >= 4 && dias <= 7) ventana = 2;
        else if (dias >= 8 && dias <= 30) ventana = 1;
    }
    if (grupo === GRUPO_REPASO.POR_COBRAR && ventana < 1) ventana = 1;
    return PESO_VALOR_GRUPO[grupo] * ventana;
}

// Estado del afiliado por días de inactividad → color + label, para que el
// promotor identifique a quién reactivar. null = nunca jugó.
//   activo:  < 7 días   (verde)
//   tibio:   7–30 días  (naranja)
//   dormido: > 30 días  (rojo)
//   sin jugar: null     (gris)
export function estadoAfiliado(diasInactivo) {
    if (diasInactivo === null || diasInactivo === undefined) {
        return { color: '#64748b', label: t('afiliados.sinJugar') };
    }
    if (diasInactivo < 7) return { color: '#22c55e', label: t('afiliados.activo') };
    if (diasInactivo <= 30) return { color: '#f59e0b', label: `${diasInactivo}d` };
    return { color: '#ef4444', label: t('afiliados.dormido') };
}
